import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { paymentsAPI } from '../services/api';
import toast from 'react-hot-toast';

const PricingPage = () => {
  const [billing, setBilling] = useState('yearly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // plan key being checked out
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch plans from backend (single source of truth)
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await paymentsAPI.getPlans();
        setPlans(res.data.data.plans);
      } catch (err) {
        toast.error('Failed to load plans. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSelectPlan = async (planKey) => {
    // Free plan — just go to signup/dashboard
    if (planKey === 'starter') {
      if (user) {
        navigate('/dashboard');
      } else {
        navigate('/signup');
      }
      return;
    }

    // Paid plan — must be logged in
    if (!user) {
      toast('Please sign up or log in first.', { icon: '🔒' });
      navigate('/signup');
      return;
    }

    // Existing paid subscriber — redirect to manage subscription page
    if (user.plan && !['free', 'starter'].includes(user.plan)) {
      navigate('/manage-subscription');
      return;
    }

    // Already on this plan
    if (user.plan === planKey) {
      toast('You are already on this plan.');
      return;
    }

    setCheckoutLoading(planKey);
    try {
      const res = await paymentsAPI.createCheckoutSession(planKey, billing);
      // Redirect to Stripe Checkout
      window.location.href = res.data.data.url;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to start checkout. Please try again.';
      toast.error(message);
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ag-black-800" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col items-center px-4 py-4 md:py-6 lg:py-8 font-sans overflow-hidden">
      {/* Header */}
      <div className="text-center mb-3 md:mb-5">
        <h1 className="text-2xl md:text-3xl lg:text-5xl text-gray-900 font-light italic">
          Choose the best plan{' '}
          <span className="font-bold">for you</span>
        </h1>
      </div>

      {/* Billing Toggle */}
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <button
          onClick={() => setBilling('monthly')}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            billing === 'monthly'
              ? 'bg-primary-50 text-black  border-2 border-ag-green shadow-lg shadow-ag-green/20'
              : 'bg-ag-black-800 text-white hover:bg-ag-black-700 border-2 border-ag-black-800 hover:border-ag-green/50'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBilling('yearly')}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
            billing === 'yearly'
              ? 'bg-primary-50 text-black  border-2 border-ag-green shadow-lg shadow-ag-green/20'
              : 'bg-ag-black-800 text-white hover:bg-ag-black-700 border-2 border-ag-black-800 hover:border-ag-green/50'
          }`}
        >
          Yearly
        </button>
      </div>

      {/* Plans Grid */}
      <div className="flex-1 min-h-0 w-full max-w-5xl">
        <div className="flex md:grid md:grid-cols-3 gap-4 md:gap-5 h-full overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-2 md:pb-0">
          {plans.map((plan) => {
            const pricing = plan[billing];
            const isHighlighted = plan.highlight;
            const isCurrentPlan = user?.plan === plan.key || (user?.plan === 'free' && plan.key === 'starter');
            const isCheckingOut = checkoutLoading === plan.key;

            return (
              <div
                key={plan.key}
                className={`relative flex flex-col rounded-2xl p-5 md:p-6 transition-all duration-200 snap-center flex-shrink-0 w-[85vw] md:w-auto ${
                  isHighlighted
                    ? 'bg-ag-black-800 text-white shadow-xl scale-105 z-10 border-2 border-ag-black-700'
                    : 'bg-white text-gray-900 shadow-md border border-gray-200'
                }`}
              >
                {/* Plan Name */}
                <h2
                  className={`text-base md:text-lg font-bold mb-2 md:mb-3 ${
                    isHighlighted ? 'text-ag-green' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h2>

                {/* Price */}
                <div className="mb-3 md:mb-4">
                  <span
                    className={`text-3xl md:text-4xl lg:text-5xl font-bold ${
                      isHighlighted ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {pricing.display}
                  </span>
                  <span
                    className={`text-sm md:text-base ${
                      isHighlighted ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {pricing.period}
                  </span>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(plan.key)}
                  disabled={isCurrentPlan || isCheckingOut}
                  className={`w-full py-2.5 rounded-full text-sm font-semibold transition-colors duration-150 mb-4 md:mb-5 disabled:opacity-60 disabled:cursor-not-allowed ${
                    isHighlighted
                      ? 'bg-ag-green text-ag-black-800 hover:bg-ag-green/90'
                      : 'bg-ag-black-800 text-white hover:bg-ag-black-700'
                  }`}
                >
                  {isCheckingOut ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                      Redirecting...
                    </span>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    plan.cta
                  )}
                </button>

                {/* Features */}
                <div className="space-y-3 md:space-y-3.5 flex-1 min-h-0 overflow-y-auto">
                  {plan.features.map((feature, idx) => (
                    <div key={idx}>
                      <p
                        className={`font-bold text-xs md:text-sm ${
                          isHighlighted ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {feature.label}
                      </p>
                      {feature.desc && (
                        <p
                          className={`text-xs mt-0.5 whitespace-pre-line leading-relaxed ${
                            isHighlighted ? 'text-gray-400' : 'text-gray-500'
                          }`}
                        >
                          {feature.desc}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Back link */}
      <Link
        to={user ? "/dashboard" : "/"}
        className="mt-3 md:mt-5 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors flex-shrink-0"
      >
        {user ? "Back to dashboard" : "Back to home"}
      </Link>
    </div>
  );
};

export default PricingPage;
